import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

const SEL = "mt-1 w-full min-w-0 rounded-md border border-neutral-700 bg-black/50 px-2 py-2 text-sm text-white";

// Admin thread header for mini-site clients: app + REF + email + business parsed from the message header, or picked by hand.
export const MinisiteScope = ({ conv, onChanged }) => {
  const { t } = useI18n();
  const sc = conv.minisite_scope;
  const issue = conv.scope_issue;
  const [edit, setEdit] = useState(false);
  if (sc && !edit) {
    return (
      <div className="mb-2 flex min-w-0 flex-wrap items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-950/40 px-3 py-2 text-xs text-emerald-100" data-testid="conv-minisite-scope">
        <Building2 className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{t("biz.header", { app: sc.app, ref: sc.ref, email: sc.email, business: sc.business_name || "—" })}</span>
        {sc.email !== conv.developer?.email && <span className="text-amber-200" data-testid="conv-minisite-email-diff">{t("biz.emailDiff")}</span>}
        <button type="button" className="rounded-full border border-emerald-400/60 px-2.5 py-0.5" onClick={() => setEdit(true)} data-testid="conv-minisite-scope-change">{t("biz.change")}</button>
      </div>
    );
  }
  return <ScopePicker conv={conv} issue={issue} onDone={() => { setEdit(false); onChanged(); }} />;
};

const ScopePicker = ({ conv, issue, onDone }) => {
  const { t } = useI18n();
  const [keys, setKeys] = useState([]);
  const sc = conv.minisite_scope || {};
  const [v, setV] = useState({ key_id: sc.key_id || "", ref: sc.ref || issue?.ref || "", business_name: sc.business_name || issue?.business_name || "", email: sc.email || issue?.email || conv.developer?.email || "" });
  useEffect(() => { api.get("/admin/minisite/keys").then(({ data }) => setKeys(data.filter((k) => !k.revoked))).catch(() => {}); }, []);
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target.value }));
  const save = (e) => {
    e.preventDefault();
    api.put(`/admin/minisite/conversations/${conv.id}/scope`, v).then(() => { toast.success(t("biz.saved")); onDone(); }).catch((err) => toast.error(translateError(err, t)));
  };
  const msg = issue?.issue === "unknown_app" ? t("biz.unknownApp", { app: issue.app }) : t(issue?.issue === "malformed" ? "biz.malformed" : "biz.missing");
  return (
    <form onSubmit={save} className="mb-2 min-w-0 space-y-2 rounded-lg border border-amber-400/40 bg-amber-950/30 p-3 text-xs text-amber-100" data-testid="conv-minisite-picker">
      <p className="flex min-w-0 items-start gap-2 break-words" data-testid="conv-minisite-notice"><Info className="mt-0.5 h-4 w-4 shrink-0" /> <span className="min-w-0">{msg}</span></p>
      <div className="grid min-w-0 gap-2 sm:grid-cols-2">
        <label className="min-w-0 text-neutral-300">{t("biz.pickApp")}
          <select required value={v.key_id} onChange={set("key_id")} className={SEL} data-testid="conv-minisite-key">
            <option value="">—</option>{keys.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
          </select></label>
        <label className="min-w-0 text-neutral-300">{t("biz.ref")}<Input required value={v.ref} onChange={set("ref")} placeholder="STP-XXXX-XXXX" className="mt-1 bg-black/50 uppercase" data-testid="conv-minisite-ref" /></label>
        <label className="min-w-0 text-neutral-300">{t("biz.email")}<Input type="email" value={v.email} onChange={set("email")} className="mt-1 bg-black/50" data-testid="conv-minisite-email" /></label>
        <label className="min-w-0 text-neutral-300">{t("biz.business")}<Input value={v.business_name} onChange={set("business_name")} className="mt-1 bg-black/50" data-testid="conv-minisite-business" /></label>
      </div>
      <button type="submit" className="btn-gold btn-sm" data-testid="conv-minisite-save">{t("biz.save")}</button>
    </form>
  );
};

// Offer form (locked to app key + email): which REFs of this owner the plan covers.
export const BusinessRefsField = ({ scope, refs, setRefs }) => {
  const { t } = useI18n();
  const [known, setKnown] = useState([]);
  const [add, setAdd] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/admin/minisite/businesses", { params: { key_id: scope.key_id, email: scope.email } }).then(({ data }) => setKnown(data)).catch(() => {});
  }, [scope.key_id, scope.email]);
  const all = [...new Map([...known.map((b) => [b.ref, b]), ...refs.map((r) => [r, known.find((b) => b.ref === r) || { ref: r }])]).values()];
  const shown = all.filter((b) => !q || `${b.ref} ${b.business_name || ""}`.toLowerCase().includes(q.toLowerCase()));
  const toggle = (r) => setRefs(refs.includes(r) ? refs.filter((x) => x !== r) : [...refs, r]);
  const addRef = () => {
    const r = add.trim().toUpperCase();
    if (/^[A-Z0-9][A-Z0-9-]{2,39}$/.test(r) && !refs.includes(r)) setRefs([...refs, r]);
    setAdd("");
  };
  return (
    <div className="min-w-0 space-y-2 rounded-lg border border-emerald-500/30 p-2.5" data-testid="offer-business">
      <p className="break-words text-xs font-semibold text-emerald-200" data-testid="offer-business-locked">{t("biz.locked", { app: scope.app, email: scope.email })}</p>
      <p className="text-xs text-neutral-300" data-testid="offer-business-count">{t("biz.refs", { n: refs.length })}</p>
      {all.length > 8 && <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("biz.search")} className="h-8 bg-black/50 text-xs" data-testid="offer-business-search" />}
      <ul className="max-h-56 min-w-0 space-y-1 overflow-y-auto">
        {shown.map((b) => (
          <li key={b.ref}><label className="flex min-w-0 items-center gap-2 text-xs text-neutral-200">
            <input type="checkbox" checked={refs.includes(b.ref)} onChange={() => toggle(b.ref)} className="accent-emerald-400" data-testid={`offer-business-ref-${b.ref}`} />
            <span className="min-w-0 [overflow-wrap:anywhere]"><b>{b.ref}</b>{b.business_name ? ` · ${b.business_name}` : ""}</span>
          </label></li>
        ))}
      </ul>
      <div className="flex min-w-0 flex-wrap gap-2">
        <Input value={add} onChange={(e) => setAdd(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRef(); } }} placeholder={t("biz.addRef")} className="h-8 min-w-0 flex-1 basis-40 bg-black/50 text-xs uppercase" data-testid="offer-business-add-input" />
        <button type="button" className="rounded-full border border-emerald-400/60 px-3 py-1 text-xs text-emerald-100" onClick={addRef} data-testid="offer-business-add">{t("biz.add")}</button>
      </div>
    </div>
  );
};

export const splitPool = (total, refs) => {
  const n = refs.length || 1;
  const base = Math.floor(total / n);
  const rest = total % n;
  return refs.map((r, i) => base + (i < rest ? 1 : 0));
};
